import React, { createContext, useContext, useState, useEffect } from 'react';
import { getToken, removeToken, getTokenPayload } from '../utils/auth';
import { flushFCMToken } from '../services/pushNotifications';

const AppContext = createContext();

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) throw new Error('useAppContext must be used within an AppProvider');
  return context;
};

function readInitialAuth() {
  const payload = getTokenPayload();
  if (payload && payload.exp && payload.exp > Date.now() / 1000) {
    return {
      user: { name: payload.name || 'User', email: payload.email, driverId: payload.driverId || null },
      role: payload.role || null,
      isAuthenticated: true,
    };
  }
  if (payload) removeToken();
  return { user: null, role: null, isAuthenticated: false };
}

function readInitialTheme() {
  const t = localStorage.getItem('fleet_theme') || 'light';
  document.documentElement.setAttribute('data-theme', t);
  return t;
}

export const AppProvider = ({ children }) => {
  const init                              = readInitialAuth();
  const [user, setUser]                   = useState(init.user);
  const [role, setRole]                   = useState(init.role);
  const [isAuthenticated, setIsAuthenticated] = useState(init.isAuthenticated);
  const [theme, setTheme]                 = useState(readInitialTheme);
  const [notifications, setNotifications] = useState([]);
  const [searchQuery, setSearchQuery]     = useState('');
  const [loading, setLoading]             = useState(false);
  const [pageResetKey, setPageResetKey]   = useState(0);
  const authReady                         = true; // auth is read synchronously — no blank-screen phase

  // Bumped when a nav link for the CURRENTLY active route is clicked again —
  // pages don't reflect their internal "detail view" state in the URL, so
  // re-clicking the same link wouldn't otherwise reset them back to the list view.
  const bumpPageReset = () => setPageResetKey((k) => k + 1);

  const login = (token) => {
    const payload = (() => {
      try { return JSON.parse(atob(token.split('.')[1])); } catch { return null; }
    })();
    if (payload) {
      setUser({ name: payload.name || 'User', email: payload.email, driverId: payload.driverId || null });
      setRole(payload.role || null);
      setIsAuthenticated(true);
      // Upload any FCM token that arrived before login
      flushFCMToken().catch(() => {});
    }
  };

  const logout = () => {
    removeToken();
    setUser(null);
    setRole(null);
    setIsAuthenticated(false);
  };

  const toggleTheme = () => {
    const next = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    localStorage.setItem('fleet_theme', next);
    document.documentElement.setAttribute('data-theme', next);
  };

  const addNotification = (notification) => {
    setNotifications(prev => [{ id: Date.now(), ...notification, timestamp: new Date().toISOString() }, ...prev]);
  };

  const removeNotification = (id) => setNotifications(prev => prev.filter(n => n.id !== id));
  const clearNotifications = () => setNotifications([]);
  const setGlobalLoading   = (v) => setLoading(v);

  const value = {
    user,
    role,
    isAdmin: role === 'admin',
    isAuthenticated,
    authReady,
    theme,
    notifications,
    searchQuery,
    loading,
    pageResetKey,
    bumpPageReset,
    login,
    logout,
    toggleTheme,
    addNotification,
    removeNotification,
    clearNotifications,
    setGlobalLoading,
    setSearchQuery,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export default AppContext;
